import { useState, useEffect, useContext } from 'react';
import {
    TextField,
    Typography,
    Box,
    Card, CardContent
} from '@mui/material';
import { AivagamButton } from 'components/global/AivagamButton/AivagamButton';
import { Message, ViewContext } from 'components/lib';
import axios from 'axios';

export function ContactForm(props) {
    const viewContext = useContext(ViewContext)
    const [sent, setSent] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phoneNumber: '',
        city: '',
        message: ''
    });
    const [errors, setErrors] = useState({
        name: false,
        email: false,
        phoneNumber: false,
        city: false
    });

    const validateField = (name, value) => {
        switch (name) {
            case 'name':
            case 'city':
                return value.trim() === '';
            case 'email':
                return !/^\S+@\S+\.\S+$/.test(value);
            case 'phoneNumber':
                return value && !/^\+?\d{7,15}$/.test(value);
            default:
                return false;
        }
    };
    const validateForm = (data) => {
        const newErrors = {
            name: validateField('name', data.name),
            email: validateField('email', data.email),
            phoneNumber: false,
            city: validateField('city', data.city)
        };
        setErrors(newErrors);
        return !Object.values(newErrors).some(Boolean);
    };

    useEffect(() => {
        const allValid = !validateField('name', formData.name) &&
            !validateField('email', formData.email) &&
            !validateField('phoneNumber', formData.phoneNumber) &&
            !validateField('city', formData.city);
        setIsFormValid(allValid);
    }, [formData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        setErrors(prev => ({
            ...prev,
            [name]: validateField(name, value)
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm(formData)) return;

        setLoading(true);

        try {
            const response = await axios.post('/api/utility/mail', {
                ...formData,
                type: 'individual',
            }, {
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (response.status === 200) {
                viewContext.notification.show(
                    response?.data?.message || 'Message Sent',
                    'success',
                    true
                );
                setSent(true);
            } else {
                throw new Error('Unexpected server response');
            }
        } catch (error) {
            const message =
                error.response?.data?.message ||
                error.response?.data?.error ||
                'Failed to send message';
                
                viewContext.notification.show(message, 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            style={{
                padding: '1em 2em 0 2em',
                width: '100%',
                maxWidth: '1200px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                margin: '0 auto',
                justifySelf: "center",
                backgroundColor: '#fff',
                borderRadius: '20px',
            }}
        >
            <Typography
                variant="h5"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '12px',
                    fontWeight: 600,
                    color: '#1450a0'
                }}
            >
                Contact Us
            </Typography>
            <Box
                sx={{
                    padding: '2em',
                    maxWidth: '600px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                    margin: '0 auto',
                    backgroundColor: '#fff',
                    borderRadius: '8px'
                }}
                noValidate
            >
                <div className='flex justify-center text-center'>
                    <Card elevation={2} sx={{ borderRadius: 2, minHeight: '60vh' }}>
                        <CardContent style={{ borderTop: "solid 4px #1450a0", borderRadius: 2 }}>
                            <Box sx={{ m: 2 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, textAlign: 'left', color: '#1450a0' }}>
                                    For Individuals
                                </Typography>
                                <Box sx={{ width: '60px', borderBottom: '2px solid #1450a0', pb: 1 }} />
                            </Box>
                            <div>
                                {sent ? (
                                    <Message
                                        title='Message Sent'
                                        type='success'
                                        text={`Thank you for your message, we'll be in touch as soon as possible.`}
                                    />
                                ) : (
                                    <Box component="form" onSubmit={handleSubmit}>
                                        <TextField
                                            label="Name"
                                            margin="normal"
                                            size="small"
                                            required
                                            id="name"
                                            placeholder="Your name"
                                            name="name"
                                            autoComplete="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            error={errors.name}
                                            helperText={errors.name && "Please enter your name"}
                                            sx={{ width: '100%' }}
                                        />

                                        <TextField
                                            label="Email"
                                            margin="normal"
                                            size="small"
                                            required
                                            id="email"
                                            placeholder="Your email address"
                                            name="email"
                                            autoComplete="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            error={errors.email}
                                            helperText={errors.email && "Please enter a valid email address"}
                                            sx={{ width: '100%' }}
                                        />

                                        <TextField
                                            label="Phone Number"
                                            margin="normal"
                                            size="small"
                                            sx={{ width: '100%' }}
                                            id="phoneNumber"
                                            placeholder="Your phone number"
                                            name="phoneNumber"
                                            type='tel'
                                            autoComplete="tel"
                                            value={formData.phoneNumber}
                                            onChange={handleChange}
                                            error={errors.phoneNumber}
                                            helperText={errors.phoneNumber && "Please enter valid phone number"}
                                        />

                                        <TextField
                                            label="City"
                                            margin="normal"
                                            size="small"
                                            required
                                            sx={{ width: '100%' }}
                                            id="city"
                                            placeholder="Your city"
                                            name="city"
                                            autoComplete="address-level3"
                                            value={formData.city}
                                            onChange={handleChange}
                                            error={errors.city}
                                            helperText={errors.city && "Please enter your city"}
                                        />

                                        <TextField
                                            label="Message"
                                            name="message"
                                            id="individualMessage"
                                            margin="normal"
                                            size="small"
                                            placeholder="How can we help you?"
                                            value={formData.message}
                                            onChange={handleChange}
                                            multiline
                                            rows={4}
                                            sx={{ width: '100%' }}
                                        />
                                        <AivagamButton
                                            buttonText={loading ? 'Sending...' : 'Send Message'}
                                            type="confirm"
                                            disabled={loading || !isFormValid}
                                            classStyles={{
                                                alignItems: 'center',
                                                fontWeight: 600,
                                                fontSize: '100%',
                                                width: '50%',
                                                marginTop: '1em'
                                            }}
                                        />
                                    </Box>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </Box>
        </div>
    );
}